
import { BlastEvent, SimulationConfig } from './types';
import { drawGravityDust } from './break/GravityDust';
import { drawCollisionEffect as dce } from './break/CollisionEffects';
import { getRadiusAtAngle, drawBlastShape } from './break/BreakUtils';

// Re-export drawCollisionEffect from the new location to maintain compatibility
export const drawCollisionEffect = dce;

export const drawBlastEvent = (
  ctx: CanvasRenderingContext2D,
  e: BlastEvent,
  config: SimulationConfig,
  globalHue: number,
  canvasWidth: number,
  canvasHeight: number,
  camera: { x: number, y: number, scale: number }
) => {
  const elapsed = Date.now() - e.startTime;
  if (elapsed > e.duration) return;
  
  const progress = elapsed / e.duration;
  const timeSeconds = elapsed / 1000; // Real-time for physics
  
  const anim = e.animationType || 'EXPLOSION';
  const useRGB = config.rgbLighting && config.useFXRGB;
  
  ctx.save();
  ctx.globalAlpha = 1 - progress;
  const blastHue1 = (globalHue + progress * 720) % 360; 
  ctx.strokeStyle = useRGB ? `hsl(${blastHue1}, 100%, 60%)` : config.blastColor;
  ctx.fillStyle = ctx.strokeStyle;

  if (anim === 'GRAVITY_DUST') {
      drawGravityDust(ctx, e, config, progress, timeSeconds);
      ctx.restore();
      return;
  }

  switch (anim) {
    case 'EXPLOSION': {
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      drawBlastShape(ctx, e, e.maxRadius * easedProgress);
      ctx.lineWidth = 20 * (1 - progress); ctx.stroke();
      break;
    }
    case 'RING_EXPAND': {
      const easedProgress = 1 - Math.pow(1 - progress, 4);
      drawBlastShape(ctx, e, e.maxRadius * (1 + easedProgress));
      ctx.lineWidth = 40 * (1 - progress); ctx.stroke();
      break;
    }
    case 'SHATTER': {
      const easedProgress = 1 - Math.pow(1 - progress, 2);
      for (let i = 0; i < 20; i++) {
          const angle = (i / 20) * Math.PI * 2 + (i * 0.5);
          const distBase = e.maxRadius + easedProgress * 150;
          const r = getRadiusAtAngle(angle, e, distBase);
          
          const sx = e.pos.x + Math.cos(angle)*r;
          const sy = e.pos.y + Math.sin(angle)*r;
          ctx.save(); ctx.translate(sx, sy); ctx.rotate(progress * 10);
          const size = 12 * (1-progress);
          ctx.beginPath(); ctx.rect(-size/2, -size/2, size, size); ctx.fill(); ctx.restore();
      }
      break;
    }
    case 'GLOW_FADE': {
      drawBlastShape(ctx, e, e.maxRadius * (1 + progress * 0.5));
      const g = ctx.createRadialGradient(e.pos.x, e.pos.y, e.maxRadius * 0.8, e.pos.x, e.pos.y, e.maxRadius * (1.2 + progress));
      g.addColorStop(0, '#fff'); g.addColorStop(0.2, ctx.strokeStyle as string); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fill();
      break;
    }
    case 'STARDUST': {
      for (let i = 0; i < 30; i++) {
        const angle = (i * 137.5) * Math.PI / 180;
        const dist = e.maxRadius + progress * 150;
        const r = getRadiusAtAngle(angle + progress * 2, e, dist);
        const px = e.pos.x + Math.cos(angle + progress * 2) * r;
        const py = e.pos.y + Math.sin(angle + progress * 2) * r;
        const sz = 4 * (1 - progress);
        ctx.beginPath(); ctx.arc(px, py, sz, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'VORTEX': {
      for (let i = 0; i < 20; i++) {
        const pOffset = i / 20;
        const p = (progress + pOffset) % 1;
        const angle = p * Math.PI * 8 + (i * 0.4);
        const radiusBase = e.maxRadius * (1 - p * 0.5);
        const r = getRadiusAtAngle(angle, e, radiusBase);
        const px = e.pos.x + Math.cos(angle) * r;
        const py = e.pos.y + Math.sin(angle) * r;
        ctx.beginPath(); ctx.arc(px, py, 8 * (1 - p), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'PIXEL_BURST': {
      const count = 40;
      for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dist = e.maxRadius + progress * 200;
          const r = getRadiusAtAngle(angle, e, dist);
          const size = 10 * (1 - progress);
          ctx.fillRect(e.pos.x + Math.cos(angle) * r - size/2, e.pos.y + Math.sin(angle) * r - size/2, size, size);
      }
      break;
    }
    case 'SONIC_BOOM': {
      ctx.lineWidth = 30 * (1 - progress);
      drawBlastShape(ctx, e, e.maxRadius + progress * 200);
      ctx.stroke();
      break;
    }
    case 'FIREWORK': {
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        const dist = e.maxRadius + progress * 150;
        const r = getRadiusAtAngle(angle, e, dist);
        const cx = e.pos.x + Math.cos(angle) * r;
        const cy = e.pos.y + Math.sin(angle) * r;
        ctx.beginPath(); ctx.arc(cx, cy, 5 * (1-progress), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'GHOST_ECHO': {
      for (let i = 1; i <= 3; i++) {
        const p = Math.max(0, progress - i * 0.1);
        if (p <= 0) continue;
        ctx.globalAlpha = (1 - p) * 0.5;
        ctx.lineWidth = 5;
        drawBlastShape(ctx, e, e.maxRadius * (1 + p));
        ctx.stroke();
      }
      break;
    }
    case 'SHARD_STORM': {
      for (let i = 0; i < 30; i++) {
        const a = (i * 137.5) * Math.PI / 180;
        const d = e.maxRadius + progress * 300;
        const r = getRadiusAtAngle(a, e, d);
        const px = e.pos.x + Math.cos(a) * r;
        const py = e.pos.y + Math.sin(a) * r;
        ctx.beginPath(); ctx.arc(px, py, 3 * (1-progress), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'NEBULA': {
      drawBlastShape(ctx, e, e.maxRadius * (1 + progress));
      ctx.fill();
      break;
    }
    case 'DATA_STREAM': {
      const count = 20;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2;
        const dist = e.maxRadius + progress * 150;
        const r = getRadiusAtAngle(angle, e, dist);
        const px = e.pos.x + Math.cos(angle) * r;
        const py = e.pos.y + Math.sin(angle) * r;
        ctx.fillText(Math.random() > 0.5 ? "1" : "0", px, py);
      }
      break;
    }
    case 'RIPPLE_WAVE': {
      for (let i = 0; i < 4; i++) {
        const p = (progress + i * 0.2) % 1;
        ctx.globalAlpha = (1 - p);
        drawBlastShape(ctx, e, e.maxRadius + p * 150);
        ctx.stroke();
      }
      break;
    }
    case 'SHOCKWAVE': {
      drawBlastShape(ctx, e, e.maxRadius + progress * 250);
      ctx.lineWidth = 10 * (1 - progress);
      ctx.stroke();
      break;
    }
    case 'GLITCH_SQUARE': {
      const sz = 20 * (1 - progress);
      for(let i=0; i<12; i++){
        const angle = (i / 12) * Math.PI * 2;
        const dist = e.maxRadius + (Math.random() - 0.5) * 50;
        const r = getRadiusAtAngle(angle, e, dist);
        const ox = Math.cos(angle) * r;
        const oy = Math.sin(angle) * r;
        ctx.strokeRect(e.pos.x + ox - sz/2, e.pos.y + oy - sz/2, sz, sz);
      }
      break;
    }
    case 'CONFETTI': {
      for(let i=0; i<20; i++){
        const ang = (i/20) * Math.PI * 2 + progress * 5;
        const dst = e.maxRadius + progress * 200;
        const r = getRadiusAtAngle(ang, e, dst);
        const cx = e.pos.x + Math.cos(ang) * r;
        const cy = e.pos.y + Math.sin(ang) * r;
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(progress * 10);
        ctx.fillRect(-5,-2,10,4); ctx.restore();
      }
      break;
    }
    case 'ENERGY_ORBS': {
      for(let i=0; i<8; i++){
        const ang = (i/8) * Math.PI * 2 + progress * 2;
        const dst = e.maxRadius + progress * 100;
        const r = getRadiusAtAngle(ang, e, dst);
        const cx = e.pos.x + Math.cos(ang) * r;
        const cy = e.pos.y + Math.sin(ang) * r;
        ctx.beginPath(); ctx.arc(cx, cy, 12 * (1-progress), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'FRACTAL_GROWTH': {
      for(let i=0; i<8; i++){
        const ang = (i/8) * Math.PI * 2;
        const rStart = getRadiusAtAngle(ang, e, e.maxRadius);
        ctx.beginPath(); ctx.moveTo(e.pos.x + Math.cos(ang) * rStart, e.pos.y + Math.sin(ang) * rStart);
        let lx = e.pos.x + Math.cos(ang) * rStart, ly = e.pos.y + Math.sin(ang) * rStart;
        for(let j=1; j<5; j++){
          const nx = lx + Math.cos(ang + (Math.random()-0.5)) * 30 * progress;
          const ny = ly + Math.sin(ang + (Math.random()-0.5)) * 30 * progress;
          ctx.lineTo(nx, ny);
          lx = nx; ly = ny;
        }
        ctx.stroke();
      }
      break;
    }
    case 'RAINBOW_BURST': {
      for(let i=0; i<24; i++){
        ctx.strokeStyle = `hsl(${(i * 15 + globalHue)%360}, 100%, 50%)`;
        ctx.beginPath();
        const rBase = e.maxRadius + progress * 150;
        const ang = (i/24)*Math.PI*2;
        const r = getRadiusAtAngle(ang, e, rBase);
        const cx = e.pos.x;
        const cy = e.pos.y;
        ctx.arc(cx, cy, r, (i/24)*Math.PI*2, ((i+0.5)/24)*Math.PI*2);
        ctx.lineWidth = 15 * (1-progress);
        ctx.stroke();
      }
      break;
    }
    case 'HEXAGON_GRID': {
      const side = 25 * (1-progress);
      for(let i=0; i<12; i++){
        const ang = (i/12) * Math.PI * 2;
        const dst = e.maxRadius + progress * 50;
        const r = getRadiusAtAngle(ang, e, dst);
        const hx = e.pos.x + Math.cos(ang)*r;
        const hy = e.pos.y + Math.sin(ang)*r;
        ctx.beginPath();
        for(let j=0; j<6; j++){
          const ja = (j/6)*Math.PI*2;
          ctx.lineTo(hx + Math.cos(ja)*side, hy+Math.sin(ja)*side);
        }
        ctx.closePath(); ctx.stroke();
      }
      break;
    }
    case 'PLASMA_WAVE': {
      ctx.beginPath();
      for(let a=0; a<Math.PI*2; a+=0.1){
        const baseR = e.maxRadius + progress * 100 * (1 + 0.3 * Math.sin(a*10 + progress*20));
        const r = getRadiusAtAngle(a, e, baseR);
        ctx.lineTo(e.pos.x + Math.cos(a)*r, e.pos.y + Math.sin(a)*r);
      }
      ctx.closePath(); ctx.stroke();
      break;
    }
    case 'SMOKE_RING': {
      ctx.shadowBlur = 40 * (1-progress); ctx.shadowColor = ctx.strokeStyle as string;
      drawBlastShape(ctx, e, e.maxRadius + progress * 100);
      ctx.lineWidth = 25 * (1-progress); ctx.stroke(); ctx.shadowBlur = 0;
      break;
    }
    case 'LIGHTNING_STRIKE': {
      for(let i=0; i<8; i++){
        const ang = (i/8)*Math.PI*2;
        const rStart = getRadiusAtAngle(ang, e, e.maxRadius);
        ctx.beginPath(); 
        let cx = e.pos.x + Math.cos(ang) * rStart;
        let cy = e.pos.y + Math.sin(ang) * rStart;
        ctx.moveTo(cx, cy);
        for(let j=0; j<6; j++){
          cx += Math.cos(ang + (Math.random()-0.5)) * 40 * progress;
          cy += Math.sin(ang + (Math.random()-0.5)) * 40 * progress;
          ctx.lineTo(cx, cy);
        }
        ctx.stroke();
      }
      break;
    }
    case 'SPIRAL_STARS': {
      for(let i=0; i<15; i++){
        const p = (progress + i/15) % 1;
        const ang = p * Math.PI * 6 + i;
        const dst = e.maxRadius + p * 200;
        const r = getRadiusAtAngle(ang, e, dst);
        const sx = e.pos.x + Math.cos(ang)*r;
        const sy = e.pos.y + Math.sin(ang)*r;
        ctx.font = `${30*(1-p)}px Arial`; ctx.fillText("★", sx, sy);
      }
      break;
    }
    case 'CRYSTAL_GROWTH': {
      for(let i=0; i<10; i++){
        const ang = (i/10)*Math.PI*2 + progress;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const cx = e.pos.x + Math.cos(ang) * r;
        const cy = e.pos.y + Math.sin(ang) * r;
        ctx.beginPath(); ctx.moveTo(cx, cy);
        ctx.lineTo(cx + 15 * (1-progress), cy - 30 * (1-progress));
        ctx.lineTo(cx + 30 * (1-progress), cy);
        ctx.closePath(); ctx.fill();
      }
      break;
    }
    case 'GALAXY_SWIRL': {
      for(let i=0; i<60; i++){
        const p = (progress + i/60) % 1;
        const ang = p * Math.PI * 12 + i;
        const dst = e.maxRadius * (1 + p * 0.5);
        const r = getRadiusAtAngle(ang, e, dst);
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(ang)*r, e.pos.y + Math.sin(ang)*r, 3*(1-p), 0, Math.PI*2); ctx.fill();
      }
      break;
    }
    case 'PHANTOM_RINGS': {
      for(let i=0; i<5; i++){
        const p = (progress + i * 0.1) % 1;
        drawBlastShape(ctx, e, e.maxRadius + p * 200);
        ctx.globalAlpha = (1-p) * 0.6; ctx.lineWidth = 10 * (1-p); ctx.stroke();
      }
      break;
    }
    case 'METEOR_SHOWER': {
      for(let i=0; i<12; i++){
        const ang = (i/12)*Math.PI*2;
        const startDst = e.maxRadius;
        const endDst = e.maxRadius + progress * 400;
        const rStart = getRadiusAtAngle(ang, e, startDst);
        const rEnd = getRadiusAtAngle(ang, e, endDst);
        
        ctx.beginPath();
        ctx.moveTo(e.pos.x + Math.cos(ang)*rStart, e.pos.y + Math.sin(ang)*rStart);
        ctx.lineTo(e.pos.x + Math.cos(ang)*rEnd, e.pos.y + Math.sin(ang)*rEnd);
        ctx.lineWidth = 6 * (1-progress); ctx.stroke();
      }
      break;
    }
    case 'QUANTUM_LEAP': {
      for(let i=0; i<20; i++){
        if(Math.random() > 0.6) {
          const ang = Math.random() * Math.PI * 2;
          const dst = e.maxRadius + (Math.random() - 0.5) * 100 * progress;
          const r = getRadiusAtAngle(ang, e, dst);
          ctx.fillRect(e.pos.x + Math.cos(ang) * r, e.pos.y + Math.sin(ang) * r, 8, 8);
        }
      }
      break;
    }
    case 'FIRE_SPIRAL': {
      ctx.strokeStyle = `hsl(${(progress*150 + 20)%60}, 100%, 50%)`;
      ctx.beginPath();
      for(let a=0; a<Math.PI*6; a+=0.1){
        const rBase = e.maxRadius + progress * 200 * (a / (Math.PI*6));
        const ang = a + progress*15;
        const r = getRadiusAtAngle(ang, e, rBase);
        ctx.lineTo(e.pos.x + Math.cos(ang)*r, e.pos.y + Math.sin(ang)*r);
      }
      ctx.stroke();
      break;
    }
    case 'AURORA_BOREALIS': {
      ctx.save(); ctx.beginPath();
      for(let a=0; a<Math.PI*2; a+=0.1){
          const rBase = e.maxRadius + Math.sin(a * 5 + progress * 10) * 40 * (1-progress);
          const r = getRadiusAtAngle(a, e, rBase);
          if (a === 0) ctx.moveTo(e.pos.x + Math.cos(a)*r, e.pos.y + Math.sin(a)*r);
          else ctx.lineTo(e.pos.x + Math.cos(a)*r, e.pos.y + Math.sin(a)*r);
      }
      ctx.closePath(); ctx.lineWidth = 30 * (1-progress); ctx.globalAlpha = (1-progress) * 0.4; ctx.stroke(); ctx.restore();
      break;
    }
    case 'DEEP_SEA_BUBBLES': {
      for(let i=0; i<16; i++){
        const ang = (i/16)*Math.PI*2 + progress;
        const dst = e.maxRadius + progress * 200;
        const r = getRadiusAtAngle(ang, e, dst);
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(ang)*r, e.pos.y + Math.sin(ang)*r, 15*(1-progress), 0, Math.PI*2); ctx.stroke();
      }
      break;
    }
    case 'TIME_WARP': {
      const p = progress < 0.5 ? progress * 2 : 2 - progress * 2;
      for(let i=0; i<32; i++){
        const ang = (i/32)*Math.PI*2;
        const rStart = getRadiusAtAngle(ang, e, e.maxRadius);
        const rEnd = getRadiusAtAngle(ang, e, e.maxRadius + 150 * p);
        ctx.beginPath(); ctx.moveTo(e.pos.x + Math.cos(ang) * rStart, e.pos.y + Math.sin(ang) * rStart);
        ctx.lineTo(e.pos.x + Math.cos(ang)*rEnd, e.pos.y + Math.sin(ang)*rEnd);
        ctx.lineWidth = 3; ctx.stroke();
      }
      break;
    }
    case 'PHYSICS_SHATTER': {
      const segments = 60; 
      const t = timeSeconds; // Physics time
      for (let i = 0; i < segments; i++) {
        const startAng = (i / segments) * Math.PI * 2;
        const endAng = ((i + 1) / segments) * Math.PI * 2;
        const midAng = (startAng + endAng) / 2;
        const rStart = getRadiusAtAngle(startAng, e, e.maxRadius);
        const rEnd = getRadiusAtAngle(endAng, e, e.maxRadius);
        const rMid = getRadiusAtAngle(midAng, e, e.maxRadius);
        const xStart = Math.cos(startAng) * rStart;
        const yStart = Math.sin(startAng) * rStart;
        const xEnd = Math.cos(endAng) * rEnd;
        const yEnd = Math.sin(endAng) * rEnd;
        const xMid = Math.cos(midAng) * rMid;
        const yMid = Math.sin(midAng) * rMid;
        const vx = Math.cos(midAng) * 150 * t;
        const vy = Math.sin(midAng) * 150 * t + 1000 * t * t; 
        ctx.save();
        const centerX = e.pos.x + xMid + vx;
        const centerY = e.pos.y + yMid + vy;
        ctx.translate(centerX, centerY);
        const thickness = 15 * (1 - progress * 0.3);
        const safeScale = (r: number) => r > 0.001 ? (r - thickness) / r : 0;
        const rScaleStart = safeScale(rStart);
        const rScaleEnd = safeScale(rEnd);
        const rScaleMid = safeScale(rMid);
        const xStartIn = xStart * rScaleStart;
        const yStartIn = yStart * rScaleStart;
        const xEndIn = xEnd * rScaleEnd;
        const yEndIn = yEnd * rScaleEnd;
        const xMidIn = xMid * rScaleMid;
        ctx.beginPath();
        ctx.moveTo(xStart - xMid, yStart - yMid);
        ctx.lineTo(0, 0);
        ctx.lineTo(xEnd - xMid, yEnd - yMid);
        ctx.lineTo(xEndIn - xMid, yEndIn - yMid);
        ctx.lineTo(xMidIn - xMid, yMid * rScaleMid - yMid); 
        ctx.lineTo(xStartIn - xMid, yStartIn - yMid);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      break;
    }
    case 'GRAVITY_CRUMBLE': {
      const blocks = 30;
      const t = timeSeconds;
      for (let i = 0; i < blocks; i++) {
        const ang = (i / blocks) * Math.PI * 2;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const bx = Math.cos(ang) * r;
        const by = Math.sin(ang) * r;
        const vx = Math.cos(ang) * 50 * t;
        const vy = Math.sin(ang) * 50 * t + 1200 * t * t;
        const sz = 18 * (1 - progress);
        ctx.save(); ctx.translate(e.pos.x + bx + vx, e.pos.y + by + vy); ctx.rotate(t * 8);
        ctx.fillRect(-sz / 2, -sz / 2, sz, sz); ctx.restore();
      }
      break;
    }
    case 'EXPLOSIVE_SHARDS': {
      const shardCount = 40;
      const t = timeSeconds;
      for (let i = 0; i < shardCount; i++) {
        const ang = (i / shardCount) * Math.PI * 2;
        const dist = e.maxRadius + t * 500;
        const r = getRadiusAtAngle(ang, e, dist);
        const sx = e.pos.x + Math.cos(ang) * r;
        const sy = e.pos.y + Math.sin(ang) * r;
        ctx.beginPath(); ctx.moveTo(sx, sy);
        ctx.lineTo(sx + 15 * Math.cos(ang + 0.5), sy + 15 * Math.sin(ang + 0.5));
        ctx.lineTo(sx + 15 * Math.cos(ang - 0.5), sy + 15 * Math.sin(ang - 0.5));
        ctx.closePath(); ctx.fill();
      }
      break;
    }
    case 'BLACK_HOLE_EAT': {
      const pInv = 1 - progress;
      const swirl = progress * Math.PI * 10;
      for (let i = 0; i < 20; i++) {
        const baseAng = (i / 20) * Math.PI * 2;
        const ang = baseAng + swirl;
        const distBase = e.maxRadius * pInv;
        const r = getRadiusAtAngle(ang, e, distBase);
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(ang) * r, e.pos.y + Math.sin(ang) * r, 8 * pInv, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'MOLTEN_DRIP': {
      const drips = 24;
      const t = timeSeconds;
      for (let i = 0; i < drips; i++) {
        const ang = (i / drips) * Math.PI * 2;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const dx = e.pos.x + Math.cos(ang) * r;
        const dy = e.pos.y + Math.sin(ang) * r + (1000 * t * t);
        ctx.beginPath(); ctx.arc(dx, dy, 12 * (1 - progress), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'BOUNCY_CHUNKS': {
      const count = 15;
      const t = timeSeconds;
      for (let i = 0; i < count; i++) {
        const ang = (i / count) * Math.PI * 2;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const bx = Math.cos(ang) * r;
        const by = Math.sin(ang) * r;
        const vx = Math.cos(ang) * 200 * t;
        let vy = Math.sin(ang) * 100 * t + 1200 * t * t;
        const bounceFloor = config.containerRadius + 100;
        if (vy > bounceFloor) vy = bounceFloor - (vy - bounceFloor) * 0.4;
        ctx.fillRect(e.pos.x + bx + vx, e.pos.y + by + vy, 10 * (1 - progress), 10 * (1 - progress));
      }
      break;
    }
    case 'PIXEL_GRAVITY': {
      const count = 60;
      const t = progress; // This one looks like noise, let's leave it as progress driven but physics based?
      // Wait, pixel gravity implies falling. Let's use t.
      const time = timeSeconds;
      for (let i = 0; i < count; i++) {
        const ang = (i / count) * Math.PI * 2;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const px = Math.cos(ang) * r;
        const py = Math.sin(ang) * r;
        const rx = (Math.random() - 0.5) * 40 * time;
        const ry = 1500 * time * time * (0.6 + Math.random() * 0.8);
        ctx.fillRect(e.pos.x + px + rx, e.pos.y + py + ry, 5, 5);
      }
      break;
    }
    case 'RADIAL_SLICE': {
      const slices = 16;
      const t = progress * 500;
      for (let i = 0; i < slices; i++) {
        const mid = (i / slices) * Math.PI * 2;
        const sx = e.pos.x + Math.cos(mid) * t;
        const sy = e.pos.y + Math.sin(mid) * t;
        const rBase = e.maxRadius;
        const r = getRadiusAtAngle(mid, e, rBase);
        ctx.save(); ctx.translate(sx, sy); 
        ctx.beginPath(); ctx.moveTo(0,0); 
        ctx.lineTo(Math.cos(mid)*r*0.2, Math.sin(mid)*r*0.2); 
        ctx.lineWidth = 12 * (1 - progress); ctx.stroke();
        ctx.restore();
      }
      break;
    }
    case 'IMPLODE_EXPLODE': {
      const s = progress < 0.25 ? (1 - progress / 0.25) : (progress - 0.25) * 4;
      drawBlastShape(ctx, e, e.maxRadius * s);
      ctx.lineWidth = 20 * (1 - progress); ctx.stroke();
      break;
    }
    case 'CHAIN_REACTION': {
      const dots = 30;
      const delay = progress * dots;
      for (let i = 0; i < dots; i++) {
        if (delay < i) continue;
        const ang = (i / dots) * Math.PI * 2;
        const r = getRadiusAtAngle(ang, e, e.maxRadius);
        const tLocal = (delay - i) / 8;
        const dy = 1000 * tLocal * tLocal;
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(ang) * r, e.pos.y + Math.sin(ang) * r + dy, 8 * (1 - progress), 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'SHRAPNEL': {
      const t = timeSeconds;
      for (let i = 0; i < 40; i++) {
        const a = (i / 40) * Math.PI * 2;
        const r = getRadiusAtAngle(a, e, e.maxRadius);
        const bx = Math.cos(a) * r;
        const by = Math.sin(a) * r;
        const v = 400 + Math.random() * 600;
        const d = t * v;
        const gy = 1200 * t * t;
        ctx.fillRect(e.pos.x + bx + Math.cos(a) * d, e.pos.y + by + Math.sin(a) * d + gy, 4, 4);
      }
      break;
    }
    case 'DEBRIS_CLOUD': {
      for (let i = 0; i < 60; i++) {
        const a = Math.random() * Math.PI * 2;
        const rBase = e.maxRadius + Math.random() * 150 * progress;
        const r = getRadiusAtAngle(a, e, rBase);
        const drift = progress * 150;
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r + drift, 3, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'ASTEROID_FIELD': {
      for (let i = 0; i < 18; i++) {
        const a = (i / 18) * Math.PI * 2 + progress;
        const dBase = e.maxRadius + progress * 700;
        const r = getRadiusAtAngle(a, e, dBase);
        ctx.save(); ctx.translate(e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r);
        ctx.rotate(progress * 15); ctx.beginPath();
        for (let j = 0; j < 5; j++) {
          const ja = (j / 5) * Math.PI * 2;
          const jr = 15 + Math.random() * 15;
          ctx.lineTo(Math.cos(ja) * jr, Math.sin(ja) * jr);
        }
        ctx.closePath(); ctx.fill(); ctx.restore();
      }
      break;
    }
    case 'VOLCANIC_BURST': {
      const t = timeSeconds;
      ctx.fillStyle = useRGB ? `hsl(${(progress * 80 + 10) % 360}, 100%, 50%)` : config.blastColor;
      for (let i = 0; i < 40; i++) {
        const a = (i / 40) * Math.PI * 2;
        const r = getRadiusAtAngle(a, e, e.maxRadius);
        const bx = Math.cos(a) * r;
        const by = Math.sin(a) * r;
        const v = 300 + Math.random() * 800;
        const d = t * v;
        const gy = 1500 * t * t;
        ctx.beginPath(); ctx.arc(e.pos.x + bx + Math.cos(a)*d, e.pos.y + by + Math.sin(a)*d + gy, 6 * (1 - progress), 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'SUPERNOVA_FRAG': {
      ctx.lineWidth = 3;
      for (let i = 0; i < 80; i++) {
        const a = Math.random() * Math.PI * 2;
        const d1Base = e.maxRadius + progress * 50;
        const d2Base = e.maxRadius + progress * 1000;
        const r1 = getRadiusAtAngle(a, e, d1Base);
        const r2 = getRadiusAtAngle(a, e, d2Base);
        ctx.beginPath(); ctx.moveTo(e.pos.x + Math.cos(a) * r1, e.pos.y + Math.sin(a) * r1);
        ctx.lineTo(e.pos.x + Math.cos(a) * r2, e.pos.y + Math.sin(a) * r2);
        ctx.stroke();
      }
      break;
    }
    case 'PRISM_SPLIT': {
      const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
      for (let i = 0; i < colors.length; i++) {
        ctx.strokeStyle = colors[i];
        const d = progress * 60 * (i + 1);
        drawBlastShape(ctx, e, e.maxRadius + d);
        ctx.lineWidth = 4 * (1 - progress); ctx.stroke();
      }
      break;
    }
    case 'FLOATING_DEBRIS': {
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2;
        const r = getRadiusAtAngle(a, e, e.maxRadius);
        const bx = Math.cos(a) * r;
        const by = Math.sin(a) * r;
        const driftX = Math.sin(progress * 8 + i) * 80;
        const driftY = -progress * 400;
        ctx.fillRect(e.pos.x + bx + driftX, e.pos.y + by + driftY, 8, 8);
      }
      break;
    }
    case 'VORTEX_SHATTER': {
      const count = 30;
      const rot = progress * Math.PI * 15;
      for (let i = 0; i < count; i++) {
        const baseAng = (i / count) * Math.PI * 2;
        const a = baseAng + rot;
        const d = e.maxRadius + progress * 200;
        const r = getRadiusAtAngle(a, e, d);
        ctx.beginPath(); ctx.arc(e.pos.x + Math.cos(a) * r, e.pos.y + Math.sin(a) * r, 6, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 'NEBULA_DRIFT': {
      for (let i = 0; i < 8; i++) {
        ctx.globalAlpha = (1 - progress) * 0.25;
        drawBlastShape(ctx, e, e.maxRadius * (1 + progress * i * 0.5));
        ctx.fill();
      }
      break;
    }
    case 'COMET_TAIL': {
      for (let i = 0; i < 40; i++) {
        const sa = Math.random() * Math.PI * 2;
        const d = progress * (800 + Math.random() * 800);
        ctx.fillRect(e.pos.x + Math.cos(sa) * d, e.pos.y + Math.sin(sa) * d, 4, 4);
      }
      break;
    }
  }
  ctx.restore();
};
